import type { QueryClient } from '@tanstack/react-query'
import {
  aplicaEstadoNoDetalhe,
  invalidaContato,
  invalidaListagensDeContatos,
  revalidaDetalheDoContato,
} from './contactCacheKeys'
import type { ContactStateChangedEvent, NewContactEvent } from '@/types/SocketEvents'

/**
 * O que o usuário PODE VER, por eixo de estado de contato.
 *
 * Chega por ARGUMENTO, nunca por hook: esta fábrica vive fora do provider justamente
 * pra ser testável sem `AuthProvider` + socket + query client. Precedente idêntico no
 * repo: `useContactsUrlState` recebe `{vistasPermitidas, podeBlocked}` pelo mesmo
 * motivo.
 *
 * **Obrigatório, sem default.** Um default fail-closed faria "esqueci de passar os
 * gates" num sítio de registro novo virar degradação silenciosa — `blocked_on` velho
 * pra sempre, sem erro nenhum.
 */
export interface GatesDeEstado {
  /** `contacts:blocked:read` */
  podeVerBloqueados: boolean
  /** `contacts:deleted:read` — fora de TODO preset do back. */
  podeVerDeletados: boolean
}

/**
 * O refetch do detalhe é decidível SEM chamar o back: o 403 só acontece quando o
 * contato entra num estado que este user não enxerga, e o payload do evento diz qual é
 * o estado novo.
 */
function podeVerOEstadoNovo(evento: ContactStateChangedEvent, gates: GatesDeEstado): boolean {
  if (evento.blocked && !gates.podeVerBloqueados) return false
  // `active: false` É o estado deletado.
  if (!evento.active && !gates.podeVerDeletados) return false
  return true
}

/**
 * Política de invalidação dos 3 eventos de contato da room do tenant.
 *
 * Fora do provider porque as decisões de `facets` e de gate são o que erra caro aqui,
 * cada uma tem justificativa do lado do back, e dentro de um `useEffect` não haveria
 * teste possível sem montar `AuthProvider` + socket + query client.
 */
export function criaHandlersDeContato(queryClient: QueryClient, gates: GatesDeEstado) {
  return {
    /** `new_contact` — não há painel do contato recém-criado pra revalidar, mas o
     *  telefone dele pode trazer um país/DDD inédito no tenant. */
    novoContato: () => void invalidaListagensDeContatos(queryClient, { facets: true }),

    /** `contact_updated` — só o `PATCH` emite (`ContactFactory.update`), e ele
     *  aceita `name`/`email`/`address` e mais nada: sem `facets`. O `db_id` vem na
     *  RAIZ do payload. */
    contatoAtualizado: (evento: NewContactEvent) =>
      void invalidaContato(queryClient, evento.db_id),

    /** `contact_state_changed` — o back emite SEM `skip_sid`, então o ATOR também
     *  recebe.
     *
     *  **Patch primeiro, revalidação depois.** O payload traz `{blocked, active}`
     *  — exatamente o que mudou —, então o patch deixa a tela honesta no
     *  mesmo tick; inverter abriria uma janela com o `blocked` velho.
     *
     *  A revalidação existe porque `blocked_on` NÃO vem no payload e fica stale quando
     *  outro operador bloqueia. Ela é GATEADA: refetchar o detalhe de um contato que
     *  acabou de entrar num estado que este user não enxerga volta 403, e o painel
     *  trocaria sucesso por "detalhes indisponíveis". Quando o gate reprova, o
     *  comportamento é o de antes — patch sem refetch, `blocked_on` stale até remontar.
     *  Isso é correto e não é silêncio: quem não pode ver o estado novo também não pode
     *  ver o detalhamento dele.
     *
     *  Alcança SÓ `contactKeys.detail`, nunca o namespace: `events` passa por
     *  `contacts:history:read`, fora de todo preset do back — outro 403, em troca de
     *  nada.
     *
     *  `facets` sempre: o evento também sai de `delete`/`restore`, que mexem em
     *  `active` — o `$match` de `distinct_origin_pairs`. */
    estadoMudou: (evento: ContactStateChangedEvent) => {
      aplicaEstadoNoDetalhe(queryClient, evento.db_id, {
        blocked: evento.blocked,
        active: evento.active,
      })
      if (podeVerOEstadoNovo(evento, gates)) {
        void revalidaDetalheDoContato(queryClient, evento.db_id)
      }
      void invalidaListagensDeContatos(queryClient, { facets: true })
    },
  }
}
